import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Snackbar, Alert,
  MenuItem, FormControlLabel, Checkbox, Divider, CircularProgress,
} from '@mui/material';
import { Add, Delete, Upload, Public, Visibility, Edit } from '@mui/icons-material';
import api from '../../api/client';
import ZoneDrawMap, { DrawnFeature } from '../../components/ZoneDrawMap';

interface ZoneLayer {
  id: number; name: string; layer_kind: string; discipline: string;
  source: string; source_ref: string; is_active: boolean; zone_count: number;
}
interface Agency { id: number; name: string; abbreviation: string; agency_type: string; }
interface Zone {
  id: number; name: string; code: string; agency_id: number | null;
  agency_name: string; parent_agency_id: number | null; parent_agency_name: string;
  color: string; priority: number;
}
interface Preview {
  feature_count: number; fields: string[]; sample: Record<string, any> | null;
  geometry_types: string[];
}

const DISC_COLORS: Record<string, string> = { law: '#1565c0', fire: '#c62828', ems: '#2e7d32' };
const DISC_LABEL: Record<string, string> = { law: 'Law', fire: 'Fire', ems: 'EMS' };

export default function ZonesPage() {
  const [layers, setLayers] = useState<ZoneLayer[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [snack, setSnack] = useState('');

  // create-layer dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', layer_kind: 'agency', discipline: 'law', import_method: 'file' as 'file' | 'esri' | 'draw' });

  // import dialog
  const [importLayer, setImportLayer] = useState<ZoneLayer | null>(null);
  const [importSource, setImportSource] = useState<'file' | 'esri' | 'draw'>('file');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [esriUrl, setEsriUrl] = useState('');
  const [drawn, setDrawn] = useState<DrawnFeature[]>([]);
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 });
  const [mapZoom, setMapZoom] = useState(12);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState({
    name_field: '', code_field: '', fixed_agency_id: '' as string,
    agency_field: '', priority: 100, color: '#3388ff', replace_existing: true,
  });
  const fileRef = useRef<HTMLInputElement>(null);

  // view-zones dialog
  const [viewLayer, setViewLayer] = useState<ZoneLayer | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);

  const load = () => api.get('/zones/layers').then((r) => setLayers(r.data));
  useEffect(() => {
    load();
    api.get('/agencies').then((r) => setAgencies(r.data));
    api.get('/system-config/public').then((r) => {
      if (r.data.map_center_lat && r.data.map_center_lng) setMapCenter({ lat: r.data.map_center_lat, lng: r.data.map_center_lng });
      if (r.data.map_default_zoom) setMapZoom(r.data.map_default_zoom);
    }).catch(() => {});
  }, []);

  const disciplineAgencies = useMemo(
    () => (importLayer ? agencies.filter((a) => a.agency_type === importLayer.discipline) : agencies),
    [agencies, importLayer],
  );

  const createLayer = async () => {
    if (!createForm.name.trim()) { setSnack('Name is required'); return; }
    const source = createForm.import_method === 'esri' ? 'esri'
      : createForm.import_method === 'draw' ? 'drawn' : 'geojson';
    const r = await api.post('/zones/layers', {
      name: createForm.name, layer_kind: createForm.layer_kind,
      discipline: createForm.discipline, source,
    });
    const method = createForm.import_method;
    setCreateOpen(false);
    setCreateForm({ name: '', layer_kind: 'agency', discipline: 'law', import_method: 'file' });
    await load();
    // Jump straight into importing boundaries for the layer just created, on
    // the source tab the admin chose — so the source type is selected up front.
    openImport(r.data);
    setImportSource(method);
  };

  const deleteLayer = async (layer: ZoneLayer) => {
    if (!window.confirm(`Delete layer "${layer.name}" and its ${layer.zone_count} zone(s)?`)) return;
    await api.delete(`/zones/layers/${layer.id}`);
    setSnack('Layer deleted');
    load();
  };

  const openImport = (layer: ZoneLayer) => {
    setImportLayer(layer);
    setImportSource('file');
    setPendingFile(null);
    setEsriUrl('');
    setDrawn([]);
    setPreview(null);
    setMapping({ name_field: '', code_field: '', fixed_agency_id: '', agency_field: '', priority: 100, color: '#3388ff', replace_existing: true });
  };

  const runFilePreview = async (file: File) => {
    if (!importLayer) return;
    setPendingFile(file);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/zones/layers/${importLayer.id}/import/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
    } catch (e: any) {
      setSnack(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const runEsriPreview = async () => {
    if (!importLayer || !esriUrl.trim()) return;
    setBusy(true);
    try {
      const r = await api.post('/zones/esri/preview', { url: esriUrl.trim() });
      setPreview(r.data);
    } catch (e: any) {
      setSnack(e.response?.data?.detail || 'Esri preview failed');
    } finally { setBusy(false); }
  };

  const commitImport = async () => {
    if (!importLayer) return;
    setBusy(true);
    const mapPayload = {
      name_field: mapping.name_field || null,
      code_field: mapping.code_field || null,
      fixed_agency_id: mapping.fixed_agency_id ? Number(mapping.fixed_agency_id) : null,
      agency_field: mapping.agency_field || null,
      priority: Number(mapping.priority),
      color: mapping.color,
      replace_existing: mapping.replace_existing,
    };
    try {
      let r;
      if (importSource === 'file' && pendingFile) {
        const fd = new FormData();
        fd.append('file', pendingFile);
        fd.append('mapping', JSON.stringify(mapPayload));
        r = await api.post(`/zones/layers/${importLayer.id}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else if (importSource === 'esri') {
        r = await api.post(`/zones/layers/${importLayer.id}/import-esri`, { url: esriUrl.trim(), ...mapPayload });
      } else if (importSource === 'draw') {
        const agencyName = new Map(agencies.map((a) => [a.id, a.name]));
        const features = drawn.filter((f) => f.ring.length >= 3).map((f) => ({
          type: 'Feature',
          properties: {
            name: f.name || '',
            code: f.code || '',
            agency: f.agency_id ? (agencyName.get(Number(f.agency_id)) || '') : '',
          },
          geometry: {
            type: 'Polygon',
            coordinates: [[...f.ring.map(([lat, lng]) => [lng, lat]), [f.ring[0][1], f.ring[0][0]]]],
          },
        }));
        r = await api.post(`/zones/layers/${importLayer.id}/import-drawn`, {
          features,
          name_field: 'name',
          code_field: 'code',
          fixed_agency_id: null,
          agency_field: 'agency',
          priority: Number(mapping.priority),
          color: mapping.color,
          replace_existing: mapping.replace_existing,
        });
      } else { setBusy(false); return; }
      setSnack(`Imported ${r.data.inserted}, skipped ${r.data.skipped}`);
      setImportLayer(null);
      load();
    } catch (e: any) {
      setSnack(e.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const openView = async (layer: ZoneLayer) => {
    setViewLayer(layer);
    const r = await api.get(`/zones/layers/${layer.id}/zones`);
    setZones(r.data);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Emergency Service Zones</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>New Layer</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Agency layers determine which Law/Fire/EMS agency has jurisdiction at a point; beat layers add
        the response area within one agency. Import boundaries from GeoJSON, a zipped Shapefile, or an Esri Feature Service.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Discipline</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Zones</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {layers.map((l) => (
              <TableRow key={l.id}>
                <TableCell sx={{ fontWeight: 600 }}>{l.name}</TableCell>
                <TableCell><Chip size="small" label={DISC_LABEL[l.discipline] || l.discipline} sx={{ bgcolor: DISC_COLORS[l.discipline], color: '#fff' }} /></TableCell>
                <TableCell><Chip size="small" variant="outlined" label={l.layer_kind === 'beat' ? 'Beat / Zone' : 'Agency'} /></TableCell>
                <TableCell>{l.source}{l.source_ref ? <Typography variant="caption" component="div" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>{l.source_ref}</Typography> : null}</TableCell>
                <TableCell>{l.zone_count}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="View zones" onClick={() => openView(l)}><Visibility fontSize="small" /></IconButton>
                  <IconButton size="small" title="Import boundaries" onClick={() => openImport(l)}><Upload fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" title="Delete layer" onClick={() => deleteLayer(l)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {layers.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No zone layers yet. Create one to get started.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create layer */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Zone Layer</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Layer Name" margin="normal" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. County Law ESZ" />
          <TextField select fullWidth label="Discipline" margin="normal" value={createForm.discipline} onChange={(e) => setCreateForm({ ...createForm, discipline: e.target.value })}>
            <MenuItem value="law">Law</MenuItem>
            <MenuItem value="fire">Fire</MenuItem>
            <MenuItem value="ems">EMS</MenuItem>
          </TextField>
          <TextField select fullWidth label="Layer Kind" margin="normal" value={createForm.layer_kind} onChange={(e) => setCreateForm({ ...createForm, layer_kind: e.target.value })}>
            <MenuItem value="agency">Agency (who has jurisdiction)</MenuItem>
            <MenuItem value="beat">Beat / Zone (within one agency)</MenuItem>
          </TextField>
          <TextField select fullWidth label="Boundary Source" margin="normal" value={createForm.import_method} onChange={(e) => setCreateForm({ ...createForm, import_method: e.target.value as 'file' | 'esri' | 'draw' })}>
            <MenuItem value="file">GeoJSON / Shapefile upload</MenuItem>
            <MenuItem value="esri">Esri Feature Service</MenuItem>
            <MenuItem value="draw">Draw on map</MenuItem>
          </TextField>
          <Typography variant="caption" color="text.secondary">
            After creating the layer you'll be taken straight to importing boundaries from your chosen source.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createLayer}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Import */}
      <Dialog open={!!importLayer} onClose={() => setImportLayer(null)} maxWidth={importSource === 'draw' ? 'md' : 'sm'} fullWidth>
        <DialogTitle>Import — {importLayer?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Button size="small" variant={importSource === 'file' ? 'contained' : 'outlined'} startIcon={<Upload />} onClick={() => { setImportSource('file'); setPreview(null); }}>GeoJSON / Shapefile</Button>
            <Button size="small" variant={importSource === 'esri' ? 'contained' : 'outlined'} startIcon={<Public />} onClick={() => { setImportSource('esri'); setPreview(null); }}>Esri Feature Service</Button>
            <Button size="small" variant={importSource === 'draw' ? 'contained' : 'outlined'} startIcon={<Edit />} onClick={() => { setImportSource('draw'); setPreview(null); }}>Draw on map</Button>
          </Box>

          {importSource === 'file' && (
            <Box>
              <input type="file" accept=".json,.geojson,.zip" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) runFilePreview(f); }} />
              <Button variant="outlined" onClick={() => fileRef.current?.click()}>{pendingFile ? pendingFile.name : 'Choose GeoJSON or zipped Shapefile'}</Button>
              <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                Shapefiles must be uploaded as a .zip containing the .shp, .dbf and .shx files.
              </Typography>
            </Box>
          )}
          {importSource === 'esri' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" label="Esri Feature Service layer URL" value={esriUrl} onChange={(e) => setEsriUrl(e.target.value)} placeholder="https://services.arcgis.com/.../FeatureServer/0" />
              <Button variant="outlined" onClick={runEsriPreview} disabled={!esriUrl.trim()}>Preview</Button>
            </Box>
          )}

          {importSource === 'draw' && importLayer && (
            <Box sx={{ mt: 1 }}>
              <ZoneDrawMap
                center={mapCenter}
                zoom={mapZoom}
                agencies={disciplineAgencies}
                layerKind={importLayer.layer_kind === 'beat' ? 'beat' : 'agency'}
                features={drawn}
                onChange={setDrawn}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                <TextField type="number" size="small" label="Priority (lower wins)" value={mapping.priority} onChange={(e) => setMapping({ ...mapping, priority: Number(e.target.value) })} />
                <TextField type="color" size="small" label="Color" value={mapping.color} onChange={(e) => setMapping({ ...mapping, color: e.target.value })} sx={{ width: 90 }} slotProps={{ inputLabel: { shrink: true } }} />
                <FormControlLabel control={<Checkbox checked={mapping.replace_existing} onChange={(e) => setMapping({ ...mapping, replace_existing: e.target.checked })} />} label="Replace existing" />
              </Box>
            </Box>
          )}

          {busy && <Box sx={{ textAlign: 'center', my: 2 }}><CircularProgress size={24} /></Box>}

          {preview && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2">
                {preview.feature_count} feature(s) · geometry: {preview.geometry_types.join(', ') || 'unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Map the source attributes to zone fields:
              </Typography>
              <TextField select fullWidth size="small" label="Name field" margin="dense" value={mapping.name_field} onChange={(e) => setMapping({ ...mapping, name_field: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {preview.fields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="Code field" margin="dense" value={mapping.code_field} onChange={(e) => setMapping({ ...mapping, code_field: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {preview.fields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>

              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                {importLayer?.layer_kind === 'beat' ? 'Parent agency for these beats:' : 'Agency with jurisdiction:'}
              </Typography>
              <TextField select fullWidth size="small" label="Assign all features to agency" margin="dense" value={mapping.fixed_agency_id} onChange={(e) => setMapping({ ...mapping, fixed_agency_id: e.target.value })}>
                <MenuItem value="">(use field mapping below)</MenuItem>
                {disciplineAgencies.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name} ({a.abbreviation})</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="…or match agency from field (by name/abbr)" margin="dense" value={mapping.agency_field} disabled={!!mapping.fixed_agency_id} onChange={(e) => setMapping({ ...mapping, agency_field: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {preview.fields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField type="number" size="small" label="Priority (lower wins)" value={mapping.priority} onChange={(e) => setMapping({ ...mapping, priority: Number(e.target.value) })} />
                <TextField type="color" size="small" label="Color" value={mapping.color} onChange={(e) => setMapping({ ...mapping, color: e.target.value })} sx={{ width: 90 }} slotProps={{ inputLabel: { shrink: true } }} />
              </Box>
              <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={mapping.replace_existing} onChange={(e) => setMapping({ ...mapping, replace_existing: e.target.checked })} />} label="Replace existing zones in this layer" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportLayer(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || (importSource === 'draw' ? drawn.filter((f) => f.ring.length >= 3).length === 0 : !preview)} onClick={commitImport}>Import</Button>
        </DialogActions>
      </Dialog>

      {/* View zones */}
      <Dialog open={!!viewLayer} onClose={() => setViewLayer(null)} maxWidth="md" fullWidth>
        <DialogTitle>Zones — {viewLayer?.name}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>{viewLayer?.layer_kind === 'beat' ? 'Parent Agency' : 'Agency'}</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Color</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zones.map((z) => (
                  <TableRow key={z.id}>
                    <TableCell>{z.name || '—'}</TableCell>
                    <TableCell>{z.code || '—'}</TableCell>
                    <TableCell>{(viewLayer?.layer_kind === 'beat' ? z.parent_agency_name : z.agency_name) || '—'}</TableCell>
                    <TableCell>{z.priority}</TableCell>
                    <TableCell><Box sx={{ width: 18, height: 18, borderRadius: '3px', bgcolor: z.color, border: '1px solid #0003' }} /></TableCell>
                  </TableRow>
                ))}
                {zones.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 2 }}>No zones imported yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewLayer(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
